/**
 * sign-error: sign-failure taxonomy (Phase 12, SIGNSAFE-03 / INVSIGN-04).
 *
 * Maps a rejection from the work-confirmation POST to exactly one
 * portal-owned kind. Shared with quote signing in Phase 13.
 *
 *   | kind           | meaning                                   | retryable | copy owner           |
 *   | -------------- | ----------------------------------------- | --------- | -------------------- |
 *   | ineligible     | invoice is no longer signable (409 / 422) | false     | app signingCopy.ts   |
 *   | stale-schedule | payment schedule needs office review      | false     | app signingCopy.ts   |
 *   | unavailable    | not found or not yours (401 / 403 / 404)  | false     | app signingCopy.ts   |
 *   | network        | offline, timeout, 5xx, or anything else   | true      | app signingCopy.ts   |
 *
 * Precedence (first match wins):
 *   1. `stale_payment_schedule` error_code beats any status.
 *   2. 422 with NO error_code whose message matches the stale-schedule
 *      pattern (fallback until the API ships error_code).
 *   3. 401 / 403 / 404 collapse to one neutral `unavailable` kind, so the UI
 *      never distinguishes "not found" from "not yours" (T-12-09).
 *   4. 409, 422, or the `not_signable` error_code.
 *   5. Everything else, including 5xx and no response, is `network`.
 *
 * Unlike `payment-error.ts`, this module NEVER returns or logs server message
 * text (D-15 / T-12-08). The message is read only to detect the stale-schedule
 * fallback; the caller renders portal-owned copy for the returned kind.
 */
import type { SignErrorKind } from './signing';

/**
 * [ASSUMED] TC-718 error_code values. Phase 14 swaps here only (D-06).
 * Compared by exact equality after trim + lowercase (never substring).
 */
export const SIGN_ERROR_CODES = Object.freeze({
  notSignable: 'not_signable',
  stalePaymentSchedule: 'stale_payment_schedule',
} as const);

/**
 * Stale-schedule detection on a 422 with no error_code (milestone
 * ARCHITECTURE Pattern 3 fallback). Non-global on purpose: a `/g` regex keeps
 * `lastIndex` between `.test()` calls and would skip matches.
 *
 * WR-06: schedule-specific wording only. Every API `StalePaymentScheduleError`
 * message ends "reconfigure the {program} before ...", and {program} is the
 * tenant's configurable program name, so "payment schedule" alone would miss
 * renamed programs. Generic phrases ("does not match", "needs review") are
 * left out: they are common in validation messages, and the stale-schedule
 * copy wrongly sends the customer to the office.
 */
const STALE_SCHEDULE_MESSAGE = /\bpayment schedule\b|\breconfigure the\b/i;

const UNAVAILABLE_STATUSES: ReadonlySet<number> = new Set([401, 403, 404]);
const INELIGIBLE_STATUSES: ReadonlySet<number> = new Set([409, 422]);

/** Loose axios response shape; every field is untrusted. */
interface SignErrorResponseLike {
  status?: unknown;
  data?: { error_code?: unknown; message?: unknown } | null;
}

/** Normalized, primitive-only signals read from the response. */
interface SignErrorSignals {
  status: number | undefined;
  code: string;
  message: string;
}

/** The axios `response` object, or undefined when there is none. */
function extractResponse(err: unknown): SignErrorResponseLike | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const response = (err as { response?: unknown }).response;
  return response && typeof response === 'object'
    ? (response as SignErrorResponseLike)
    : undefined;
}

/** HTTP status, numbers only. */
function extractStatus(response: SignErrorResponseLike): number | undefined {
  return typeof response.status === 'number' ? response.status : undefined;
}

/** `data.error_code`, strings only, trimmed and lowercased. */
function extractCode(response: SignErrorResponseLike): string {
  const raw = response.data?.error_code;
  return typeof raw === 'string' ? raw.trim().toLowerCase() : '';
}

/** `data.message`, strings only. Used for detection, never returned. */
function extractMessage(response: SignErrorResponseLike): string {
  const raw = response.data?.message;
  return typeof raw === 'string' ? raw : '';
}

const hasStatus = (set: ReadonlySet<number>, status: number | undefined) =>
  status !== undefined && set.has(status);

/**
 * Ordered rule table (first match wins). A flat table rather than an `if`
 * chain keeps the classifier under the complexity ceiling, mirroring
 * `payment-error.ts`.
 */
const RULES: ReadonlyArray<{
  kind: SignErrorKind;
  matches: (s: SignErrorSignals) => boolean;
}> = [
  {
    kind: 'stale-schedule',
    matches: (s) => s.code === SIGN_ERROR_CODES.stalePaymentSchedule,
  },
  {
    kind: 'stale-schedule',
    matches: (s) =>
      s.status === 422 &&
      s.code === '' &&
      STALE_SCHEDULE_MESSAGE.test(s.message),
  },
  {
    kind: 'unavailable',
    matches: (s) => hasStatus(UNAVAILABLE_STATUSES, s.status),
  },
  {
    kind: 'ineligible',
    matches: (s) =>
      hasStatus(INELIGIBLE_STATUSES, s.status) ||
      s.code === SIGN_ERROR_CODES.notSignable,
  },
];

/**
 * Classify a sign failure into one portal-owned kind. Pure: the same error
 * always yields the same kind, and the result is always one of the four
 * `SignErrorKind` strings.
 */
export function classifySignError(err: unknown): SignErrorKind {
  const response = extractResponse(err);
  if (!response) return 'network';
  const signals: SignErrorSignals = {
    status: extractStatus(response),
    code: extractCode(response),
    message: extractMessage(response),
  };
  return RULES.find((rule) => rule.matches(signals))?.kind ?? 'network';
}

/** D-14: only `network` is retryable; every other kind is terminal. */
export function isRetryableSignError(kind: SignErrorKind): boolean {
  return kind === 'network';
}
